import { useEffect, useState } from 'react';
import { Activity, Bell, BellOff, Send, Zap } from 'lucide-react';
import { useTickets } from '../../hooks/useTickets';
import { useTicketWebSocket } from '../../hooks/useTicketWebSocket';
import { TicketRowItem } from '../common/TicketRowItem';
import { apiClient } from '../../services/api';
import { bridgeService } from '../../services/bridge';
import type { TicketTab } from './TicketList';
import type { Ticket } from '../../types';

interface DashboardProps {
  onNavigateToTickets: (tab?: TicketTab) => void;
  onSelectTicket: (ticket: Ticket) => void;
  /** L2-only: open the canned-response admin library. Undefined for level<2,
   *  which both hides the card and is the real (server-enforced) gate. */
  onOpenAdmin?: () => void;
}

function getDeviceIdFromToken(): string {
  const token = localStorage.getItem('support_auth_token');
  if (!token) return '';
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.deviceId || '';
  } catch { return ''; }
}

export function Dashboard({ onNavigateToTickets, onSelectTicket, onOpenAdmin }: DashboardProps) {
  const { openTickets, stats, isLoading, refreshOpenTickets, loadStats } = useTickets();
  const openList = Array.isArray(openTickets) ? openTickets : [];

  // Default true = hide banner until check resolves (avoids flash-of-banner on load)
  const [isTelegramLinked, setIsTelegramLinked] = useState(true);
  const [notificationBotUsername, setNotificationBotUsername] = useState<string | null>(null);

  const token = localStorage.getItem('support_auth_token') || '';

  const { lastMessage: wsMessage } = useTicketWebSocket({
    ticketId: 'stats',
    authToken: token,
    enabled: true,
    onOpen: () => {
      refreshOpenTickets();
      loadStats();
      apiClient.checkTelegramLinked().then(setIsTelegramLinked);
    },
  });

  // Restore unified “recent activity” slice when the dashboard mounts (ticket list uses scoped fetches
  // and overwrites shared `openTickets` while that view is active).
  useEffect(() => {
    refreshOpenTickets();
    loadStats();
  }, [refreshOpenTickets, loadStats]);

  // Check telegram linked status and fetch config on mount
  useEffect(() => {
    apiClient.checkTelegramLinked().then(setIsTelegramLinked);
    apiClient.getConfig().then(cfg => setNotificationBotUsername(cfg.notificationBotUsername));
  }, []);

  // Re-check when user returns from another app (e.g. after linking via Telegram bot)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        apiClient.checkTelegramLinked().then(setIsTelegramLinked);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (wsMessage && (wsMessage.type === 'new_ticket' || wsMessage.type === 'ticket_updated' || wsMessage.type === 'stats_updated')) {
      refreshOpenTickets();
      loadStats();
    }
  }, [wsMessage, refreshOpenTickets, loadStats]);

  // Periodic polling fallback — refreshes every 30s as safety net for missed WebSocket updates
  useEffect(() => {
    const id = setInterval(() => {
      refreshOpenTickets();
      loadStats();
    }, 30_000);
    return () => clearInterval(id);
  }, [refreshOpenTickets, loadStats]);

  const displayStats = stats || {
    open: openList.filter(t => t.status === 'open').length,
    inProgress: openList.filter(t => t.status === 'in_progress').length,
    unread: openList.filter(t => t.hasUnreadMessages).length,
    closed: 0,
  };

  const recentTickets = openList.slice(0, 5);

  if (isLoading && openList.length === 0) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-text-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-text-secondary">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 flex flex-col gap-6 pb-8">
      {/* Page title + L2 Manage-library action (aligned to the header row, a
          discoverable alternative to the TopNavbar name chip). The button is
          hidden for level<2 (onOpenAdmin undefined); server-side requireLevel(2)
          is the real gate. */}
      <div className="pt-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0a0a0a]">Dashboard</h1>
          <p className="text-sm text-text-secondary mt-1">SVC Ticket Support </p>
        </div>
        {onOpenAdmin && (
          <button
            onClick={onOpenAdmin}
            className="shrink-0 mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border-primary bg-white text-[13px] font-medium text-[#007AFF] shadow-sm hover:shadow-md transition touch-active"
          >
            <Zap size={15} strokeWidth={2} />
            Manage
          </button>
        )}
      </div>

      {/* Telegram notify banner — shows linked or unlinked state */}
      {isTelegramLinked ? (
        <div className="w-full flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center shrink-0">
            <Bell size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800">Telegram notifications on</p>
            <p className="text-xs text-green-600 truncate">@{notificationBotUsername || 'notification bot'}</p>
          </div>
          <button
            className="flex items-center gap-1 text-xs text-green-700 hover:text-red-600 transition shrink-0"
            onClick={async () => {
              const ok = await apiClient.unlinkTelegramNotify();
              if (ok) setIsTelegramLinked(false);
            }}
          >
            <BellOff size={14} />
            <span>Unlink</span>
          </button>
        </div>
      ) : (
        <button
          className="w-full flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-left hover:bg-orange-100 transition"
          onClick={() => {
            const deviceId = getDeviceIdFromToken();
            if (deviceId && notificationBotUsername) {
              const botUsername = notificationBotUsername;
              const deepLink = `tg://resolve?domain=${botUsername}&start=${deviceId}`;
              const webFallback = `https://t.me/${botUsername}?start=${deviceId}`;
              bridgeService.openExternalLink(deepLink, 'externalApplication').catch(() => {
                window.open(webFallback, '_blank');
              });
            }
          }}
        >
          <div className="w-8 h-8 rounded-lg bg-orange-400 flex items-center justify-center shrink-0">
            <Send size={16} className="text-white" />
          </div>
          <span className="text-sm font-medium text-orange-700">Get Notify</span>
        </button>
      )}

      {/* Stats cards */}
      <div className="flex flex-col gap-3">
        {/* New Tickets — large card */}
        <button
          onClick={() => onNavigateToTickets('new')}
          className="w-full bg-white border border-border-primary rounded-xl p-4 shadow-sm text-left hover:shadow-md transition"
        >
          <div>
            <p className="text-sm font-medium text-text-secondary">New Tickets</p>
            <p className="text-4xl font-bold text-[#0a0a0a] mt-1">{displayStats.open}</p>
            <p className="text-sm text-red-500 font-medium mt-1">
              {displayStats.open === 0 ? 'All caught up' : 'No agent response yet'}
            </p>
          </div>
        </button>

        {/* In Progress + Resolved — two smaller cards */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onNavigateToTickets('in_progress')}
            className="bg-white border border-border-primary rounded-xl p-4 shadow-sm text-left hover:shadow-md transition"
          >
            <p className="text-sm font-medium text-text-secondary">In Progress</p>
            <p className="text-3xl font-bold text-[#0a0a0a] mt-1">{displayStats.inProgress}</p>
            <p className="text-xs text-orange-500 font-medium mt-1">Agent responding</p>
          </button>

          <button
            onClick={() => onNavigateToTickets('resolved')}
            className="bg-white border border-border-primary rounded-xl p-4 shadow-sm text-left hover:shadow-md transition"
          >
            <p className="text-sm font-medium text-text-secondary">Resolved</p>
            <p className="text-3xl font-bold text-[#0a0a0a] mt-1">{displayStats.closed}</p>
            <p className="text-xs text-green-600 font-medium mt-1">Closed successfully</p>
          </button>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-text-secondary" />
          <h2 className="text-base font-semibold text-[#0a0a0a]">Recent Activities</h2>
        </div>

        {recentTickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            {/* Simple empty state illustration */}
            <div className="text-5xl mb-4">🛸</div>
            <p className="text-text-tertiary text-sm">No activity yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {recentTickets.map(ticket => (
              <TicketRowItem
                key={ticket.ticketId}
                ticket={ticket}
                onClick={() => onSelectTicket(ticket)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
